import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import useAuthStore from "./store/authStore";
import useSettingsStore from "./store/settingsStore";
import { buildTheme, applyTheme } from "./themes";
import { LLM_ENABLED } from "./utils/flags";

// Layout
import AppLayout from "./components/layout/AppLayout";

// Auth
import LoginPage from "./pages/auth/LoginPage";
import CallbackPage from "./pages/auth/CallbackPage";

// App pages
import DashboardPage from "./pages/dashboard/DashboardPage";
import ProjectListPage from "./pages/projects/ProjectListPage";
import ProjectDetailPage from "./pages/projects/ProjectDetailPage";
import LayoutToolPage from "./pages/projects/LayoutToolPage";

// Blade editor
import BladeLayout from "./pages/blade/BladeLayout";
import SummaryPage from "./pages/blade/SummaryPage";
import InitPage from "./pages/blade/InitPage";
import MECOPage from "./pages/blade/MECOPage";
import BetaPage from "./pages/blade/BetaPage";
import ThicknessPage from "./pages/blade/ThicknessPage";
import EdgesPage from "./pages/blade/EdgesPage";
import CutoffPage from "./pages/blade/CutoffPage";
import View2DPage from "./pages/blade/View2DPage";
import View3DPage from "./pages/blade/View3DPage";
import CurvaturePage from "./pages/blade/CurvaturePage";
import ExportPage from "./pages/blade/ExportPage";
import TransformPage from "./pages/blade/TransformPage";
import HydraulicLayoutPage from "./pages/blade/HydraulicLayoutPage";

// Pump assembly
import AssemblyListPage from "./pages/assembly/AssemblyListPage";
import AssemblyDetailPage from "./pages/assembly/AssemblyDetailPage";

// Library
import ComponentLibraryPage from "./pages/library/ComponentLibraryPage";

// Docs
import DocsPage from "./pages/docs/DocsPage";

// Admin
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminEntitiesPage from "./pages/admin/AdminEntitiesPage";
import AdminLookupTypesPage from "./pages/admin/AdminLookupTypesPage";
import KnowledgeBasePage from "./pages/admin/KnowledgeBasePage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminCheersPage from "./pages/admin/AdminCheersPage";
import AdminStatCurvesPage from "./pages/admin/AdminStatCurvesPage";

function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuthStore();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuthStore();
  if (loading) return null;
  return user?.is_admin ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { init, isAuthenticated, user } = useAuthStore();
  const fetchSettings = useSettingsStore((s) => s.fetch);

  useEffect(() => { init(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAuthenticated) fetchSettings(); }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply theme whenever the logged-in user's entity changes
  useEffect(() => {
    if (user?.entity_theme) applyTheme(buildTheme(user.entity_theme));
  }, [user?.entity]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />

        {/* Protected */}
        <Route path="/" element={<RequireAuth><AppLayout /></RequireAuth>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="projects" element={<ProjectListPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="projects/:projectId/layout" element={<LayoutToolPage />} />

          {/* Blade editor — nested tabs */}
          <Route path="projects/:projectId/blade/:bladeId" element={<BladeLayout />}>
            <Route index element={<Navigate to="summary" replace />} />
            <Route path="summary"   element={<SummaryPage />} />
            <Route path="hydraulic" element={<HydraulicLayoutPage />} />
            <Route path="init"      element={<InitPage />} />
            <Route path="meco"      element={<MECOPage />} />
            <Route path="edges"     element={<EdgesPage />} />
            <Route path="beta"      element={<BetaPage />} />
            <Route path="thickness" element={<ThicknessPage />} />
            <Route path="cutoff"    element={<CutoffPage />} />
            <Route path="view2d"    element={<View2DPage />} />
            <Route path="view3d"    element={<View3DPage />} />
            <Route path="curvature" element={<CurvaturePage />} />
            <Route path="transform" element={<TransformPage />} />
            <Route path="export"    element={<ExportPage />} />
          </Route>

          {/* Pump assemblies */}
          <Route path="projects/:projectId/assemblies" element={<AssemblyListPage />} />
          <Route path="projects/:projectId/assemblies/:assemblyId" element={<AssemblyDetailPage />} />

          {/* Designs */}
          <Route path="designs" element={<ComponentLibraryPage />} />

          {/* Documentation */}
          <Route path="docs" element={<DocsPage />} />

          {/* Admin */}
          <Route path="admin/entities"       element={<RequireAdmin><AdminEntitiesPage /></RequireAdmin>} />
          <Route path="admin/users"          element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
          <Route path="admin/lookups"        element={<RequireAdmin><AdminLookupTypesPage /></RequireAdmin>} />
          {LLM_ENABLED && <Route path="admin/knowledge-base" element={<RequireAdmin><KnowledgeBasePage /></RequireAdmin>} />}
          {LLM_ENABLED && <Route path="admin/settings"       element={<RequireAdmin><AdminSettingsPage /></RequireAdmin>} />}
          <Route path="admin/cheers"         element={<RequireAdmin><AdminCheersPage /></RequireAdmin>} />
          <Route path="admin/stat-curves"    element={<RequireAdmin><AdminStatCurvesPage /></RequireAdmin>} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
